import React, { useState, useEffect } from 'react';
import { 
  TextField, Button, CircularProgress, Alert, Grid
} from '@mui/material';
import { useMutation, gql } from '@apollo/client';
import { StyledFormBox } from './StyledComponents';

// GraphQL mutations
const CREATE_DEPARTMENT = gql`
  mutation CreateDepartment($name: String!, $description: String) {
    createDepartment(name: $name, description: $description) {
      success
      message
      department {
        id
        name
        description
      }
    }
  }
`;

const UPDATE_DEPARTMENT = gql`
  mutation UpdateDepartment($id: ID!, $name: String, $description: String) {
    updateDepartment(id: $id, name: $name, description: $description) {
      success
      message
      department {
        id
        name
        description
      }
    }
  }
`;

function DepartmentForm({ onSuccess, initialData, isEdit = false }) {
  // État du formulaire
  const [formValues, setFormValues] = useState({
    id: initialData?.id || '',
    name: initialData?.name || '',
    description: initialData?.description || ''
  });
  
  // État des erreurs
  const [formErrors, setFormErrors] = useState({});
  
  // État des champs touchés
  const [touchedFields, setTouchedFields] = useState({});
  
  // État des messages
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Mutation pour créer un département
  const [createDepartment, { loading: createLoading }] = useMutation(CREATE_DEPARTMENT, {
    onCompleted: (data) => {
      if (data.createDepartment.success) {
        setSuccessMessage('Département créé avec succès');
        setErrorMessage('');
        
        // Réinitialiser le formulaire
        resetForm();
        
        if (onSuccess) {
          onSuccess(data.createDepartment.department);
        }
      } else {
        setErrorMessage(data.createDepartment.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Mutation pour mettre à jour un département
  const [updateDepartment, { loading: updateLoading }] = useMutation(UPDATE_DEPARTMENT, {
    onCompleted: (data) => {
      if (data.updateDepartment.success) {
        setSuccessMessage('Département mis à jour avec succès');
        setErrorMessage('');
        
        if (onSuccess) {
          onSuccess(data.updateDepartment.department);
        }
      } else {
        setErrorMessage(data.updateDepartment.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // État de chargement global
  const loading = createLoading || updateLoading;
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormValues({
      id: '',
      name: '',
      description: ''
    });
    setFormErrors({});
    setTouchedFields({});
  };
  
  // Mettre à jour les valeurs initiales lorsque initialData change
  useEffect(() => {
    if (initialData) {
      setFormValues({
        id: initialData.id || '',
        name: initialData.name || '',
        description: initialData.description || ''
      });
      // Réinitialiser les erreurs et les champs touchés
      setFormErrors({});
      setTouchedFields({});
    }
  }, [initialData]);
  
  // Gérer les changements dans les champs du formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormValues({
      ...formValues,
      [name]: value
    });
    
    // Valider le champ
    const error = validateField(name, value);
    setFormErrors({
      ...formErrors,
      [name]: error
    });
  };
  
  // Gérer la perte de focus sur un champ
  const handleBlur = (e) => {
    const { name } = e.target;
    
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
  };
  
  // Valider un champ
  const validateField = (name, value) => {
    let error = null;
    
    switch (name) {
      case 'name':
        if (!value) {
          error = 'Nom requis';
        } else if (value.length < 2) {
          error = 'Le nom doit contenir au moins 2 caractères';
        }
        break;
      default:
        break;
    }
    
    return error;
  };
  
  // Valider le formulaire entier
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    // Valider chaque champ
    Object.keys(formValues).forEach(name => {
      if (name === 'id') return; // Ignorer l'ID
      
      const error = validateField(name, formValues[name]);
      if (error) {
        errors[name] = error;
        isValid = false;
      }
    });
    
    setFormErrors(errors);
    return isValid;
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés
    const allTouched = {};
    Object.keys(formValues).forEach(name => {
      if (name === 'id') return; // Ignorer l'ID
      allTouched[name] = true;
    });
    setTouchedFields(allTouched);
    
    // Valider le formulaire
    if (validateForm()) {
      console.log("Formulaire valide, soumission en cours...");
      console.log("Valeurs du formulaire:", formValues);
      
      if (isEdit) {
        // Mettre à jour le département
        const variables = {
          id: formValues.id,
          name: formValues.name,
          description: formValues.description
        };
        
        console.log("Variables pour la mutation updateDepartment:", variables);
        
        updateDepartment({ variables })
          .then(response => {
            console.log("Réponse de la mutation updateDepartment:", response);
          })
          .catch(error => {
            console.error("Erreur lors de la mutation updateDepartment:", error);
          });
      } else {
        // Créer un nouveau département
        createDepartment({
          variables: {
            name: formValues.name,
            description: formValues.description
          }
        });
      }
    } else {
      console.log("Formulaire invalide, erreurs:", formErrors);
    }
  };
  
  return (
    <StyledFormBox>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Nom du département"
              value={formValues.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.name && Boolean(formErrors.name)}
              helperText={touchedFields.name && formErrors.name}
              margin="normal"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Description"
              value={formValues.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.description && Boolean(formErrors.description)}
              helperText={touchedFields.description && formErrors.description}
              margin="normal"
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : (isEdit ? 'Mettre à jour le département' : 'Créer le département')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </StyledFormBox>
  );
}

export default DepartmentForm; 